import {useCallback, useEffect, useId, useRef, useState} from 'react'
import {ActionList, Autocomplete, Button, FormControl, VisuallyHidden} from '@primer/react'
import {announce} from '@github-ui/aria-live'
import {OrgSelector} from '@github-ui/org-selector'
import {ReposSelector} from '@github-ui/repos-selector'
import type {
  SimpleOrganization,
  SourceType,
  WorkflowRepository,
  WorkflowsRuleMetadata,
} from '@github-ui/repos-rules-types/rules-types'
import {getOrgSuggestionsForEnterprise, getWorkflowRepoSuggestions, getWorkflowSuggestions} from '../../services/api'
import {useRelativeNavigation} from '../../hooks/use-relative-navigation'

export type WorkflowFile = {
  name: string
  path: string
}

export type WorkflowMetadataEntry = {
  workflowName?: string
  repository: WorkflowRepository
}

export type RequiredWorkflowTarget = {
  repositoryId: number
  workflowPath: string
}

const DYNAMIC_WORKFLOW_PREFIX = 'dynamic/'
const GLOB_CHARACTERS = ['*', '?', '[', ']', '{', '}', '\\']
const ORGANIZATION_LOAD_ERROR = 'Organizations could not be loaded. Try searching again.'
const REPOSITORY_LOAD_ERROR = 'Repositories could not be loaded. Try searching again.'
const WORKFLOW_LOAD_ERROR = 'Required workflows could not be loaded'

function useLatestSuggestionLoader<T>(
  loadSuggestions: (query: string) => Promise<T[]>,
  showLoadError: boolean,
  loadErrorMessage: string,
) {
  const requestIdRef = useRef(0)
  const latestRequestRef = useRef<Promise<T[]> | undefined>(undefined)
  const activeRef = useRef(true)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    activeRef.current = true
    return () => {
      activeRef.current = false
      requestIdRef.current += 1
      latestRequestRef.current = undefined
    }
  }, [])

  const loadLatestSuggestions = useCallback(
    (query: string) => {
      if (!activeRef.current) {
        return Promise.resolve<T[]>([])
      }

      const requestId = ++requestIdRef.current
      setLoadError(false)

      const request = (async () => {
        try {
          const suggestions = await loadSuggestions(query)
          if (requestId !== requestIdRef.current) {
            return await (latestRequestRef.current ?? [])
          }

          return suggestions
        } catch (error) {
          if (requestId !== requestIdRef.current) {
            return await (latestRequestRef.current ?? [])
          }
          if (!showLoadError) {
            throw error
          }

          setLoadError(true)
          announce(loadErrorMessage)
          return []
        }
      })()

      latestRequestRef.current = request
      return request
    },
    [loadErrorMessage, loadSuggestions, showLoadError],
  )

  return {loadLatestSuggestions, loadError}
}

export const workflowMetadataKey = (repositoryId: number, workflowPath: string) => `${repositoryId}/${workflowPath}`

export const buildWorkflowMetadataMap = (metadata?: WorkflowsRuleMetadata): Record<string, WorkflowMetadataEntry> =>
  Object.fromEntries(
    (Array.isArray(metadata?.workflows) ? metadata.workflows : []).flatMap(workflow =>
      workflow?.repository && typeof workflow.path === 'string'
        ? [
            [
              workflowMetadataKey(workflow.repository.id, workflow.path),
              {workflowName: workflow.name, repository: workflow.repository},
            ],
          ]
        : [],
    ),
  )

export const serializeRequiredWorkflowTarget = ({repositoryId, workflowPath}: RequiredWorkflowTarget) =>
  `required/${repositoryId}/${workflowPath}`

const isTargetableRequiredWorkflowPath = (workflowPath: string) =>
  workflowPath.length > 0 &&
  workflowPath.trim() === workflowPath &&
  !workflowPath.startsWith('/') &&
  !workflowPath.startsWith(DYNAMIC_WORKFLOW_PREFIX) &&
  !GLOB_CHARACTERS.some(character => workflowPath.includes(character))

export const parseRequiredWorkflowTarget = (value: string): RequiredWorkflowTarget | undefined => {
  const match = /^required\/([1-9]\d*)\/(.+)$/.exec(value)
  if (!match) {
    return undefined
  }

  const repositoryId = Number(match[1])
  const workflowPath = match[2]
  if (!workflowPath || !Number.isSafeInteger(repositoryId) || !isTargetableRequiredWorkflowPath(workflowPath)) {
    return undefined
  }

  return {repositoryId, workflowPath}
}

type RequiredWorkflowOrganizationFieldProps = {
  organization?: SimpleOrganization
  onChange: (organization?: SimpleOrganization) => void
  showError: boolean
  showLoadError?: boolean
  className?: string
}

export function RequiredWorkflowOrganizationField({
  organization,
  onChange,
  showError,
  showLoadError = false,
  className,
}: RequiredWorkflowOrganizationFieldProps) {
  const {resolvePath} = useRelativeNavigation()
  const labelId = useId()
  const selectedValueId = useId()
  const validationId = useId()
  const loadOrganizations = useCallback(
    (query: string) => getOrgSuggestionsForEnterprise(resolvePath('org_suggestions'), {query}),
    [resolvePath],
  )
  const {loadLatestSuggestions: queryForOrganizations, loadError} = useLatestSuggestionLoader(
    loadOrganizations,
    showLoadError,
    ORGANIZATION_LOAD_ERROR,
  )
  const displayedValidation = loadError ? ORGANIZATION_LOAD_ERROR : showError ? 'Please select an organization' : ''

  return (
    <FormControl required className={className}>
      <FormControl.Label id={labelId}>Organization</FormControl.Label>
      <VisuallyHidden id={selectedValueId}>{organization?.name ?? 'Select organization'}</VisuallyHidden>
      <OrgSelector
        selection={organization}
        orgLoader={queryForOrganizations}
        selectOrg={onChange}
        selectionVariant="single"
        additionalButtonProps={{
          alignContent: 'start',
          className: 'width-full',
          'aria-labelledby': `${labelId} ${selectedValueId}`,
          'aria-describedby': displayedValidation ? validationId : undefined,
          'aria-invalid': displayedValidation ? true : undefined,
        }}
        removeOrg={() => onChange(undefined)}
      />
      {displayedValidation && (
        <FormControl.Validation id={validationId} variant="error">
          {displayedValidation}
        </FormControl.Validation>
      )}
    </FormControl>
  )
}

type RequiredWorkflowRepositoryFieldProps = {
  organization?: SimpleOrganization
  repository?: WorkflowRepository
  onChange: (repository?: WorkflowRepository) => void
  showError: boolean
  showLoadError?: boolean
  className?: string
}

export function RequiredWorkflowRepositoryField({
  organization,
  repository,
  onChange,
  showError,
  showLoadError = false,
  className,
}: RequiredWorkflowRepositoryFieldProps) {
  const {resolvePath} = useRelativeNavigation()
  const labelId = useId()
  const selectedValueId = useId()
  const validationId = useId()
  const organizationId = organization?.id
  const loadRepositories = useCallback(
    async (query: string) => {
      const repositories = await getWorkflowRepoSuggestions(resolvePath('../workflows'), query, organizationId)
      return repositories?.map(candidate => ({...candidate, enabled: candidate.actionsSharing})) || []
    },
    [organizationId, resolvePath],
  )
  const {loadLatestSuggestions: queryForRepositories, loadError} = useLatestSuggestionLoader(
    loadRepositories,
    showLoadError,
    REPOSITORY_LOAD_ERROR,
  )
  const displayedValidation = loadError ? REPOSITORY_LOAD_ERROR : showError ? 'Please select a repository' : ''

  return (
    <FormControl required className={className}>
      <FormControl.Label id={labelId}>Repository</FormControl.Label>
      <VisuallyHidden id={selectedValueId}>{repository?.name ?? 'Select a repository'}</VisuallyHidden>
      <ReposSelector
        key={organizationId ?? 'organization-source'}
        currentSelection={repository}
        repositoryLoader={queryForRepositories}
        onSelect={onChange}
        selectionVariant="single"
        selectAllOption={false}
        additionalButtonProps={{
          alignContent: 'start',
          className: 'width-full',
          'aria-labelledby': `${labelId} ${selectedValueId}`,
          'aria-describedby': displayedValidation ? validationId : undefined,
          'aria-invalid': displayedValidation ? true : undefined,
        }}
      />
      {displayedValidation && (
        <FormControl.Validation id={validationId} variant="error">
          {displayedValidation}
        </FormControl.Validation>
      )}
    </FormControl>
  )
}

type RequiredWorkflowFileFieldProps = {
  repository: WorkflowRepository
  path: string
  onChange: (path: string, workflow?: WorkflowFile) => void
  validation?: string
  showLoadError?: boolean
  targetableOnly?: boolean
  className?: string
}

export function RequiredWorkflowFileField({
  repository,
  path,
  onChange,
  validation,
  showLoadError = false,
  targetableOnly = false,
  className,
}: RequiredWorkflowFileFieldProps) {
  const labelId = useId()
  const validationId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const {workflowOptions, loadError, retry} = useWorkflowSuggestions(repository, showLoadError, targetableOnly)
  const displayedValidation = showLoadError && loadError ? WORKFLOW_LOAD_ERROR : validation
  const retryWorkflowSuggestions = useCallback(() => {
    inputRef.current?.focus()
    retry()
  }, [retry])

  return (
    <FormControl required className={className}>
      <FormControl.Label id={labelId}>Pick a workflow file</FormControl.Label>
      <Autocomplete>
        <Autocomplete.Input
          ref={inputRef}
          value={path}
          onChange={event => {
            const nextPath = event.target.value
            onChange(
              nextPath,
              workflowOptions?.find(workflow => workflow.path === nextPath),
            )
          }}
          placeholder="path/workflow.yml"
          className="width-full"
          required
          validationStatus={displayedValidation ? 'error' : undefined}
          aria-invalid={displayedValidation ? true : undefined}
          aria-describedby={displayedValidation ? validationId : undefined}
        />
        <Autocomplete.Overlay>
          <Autocomplete.Menu
            items={
              workflowOptions?.map(workflow => ({
                id: workflow.path,
                text: workflow.path,
                children: [
                  workflow.path,
                  // Autocomplete.Menu places item children directly under ActionList.Item.
                  // eslint-disable-next-line primer-react/direct-slot-children
                  <ActionList.Description key="description" variant="block">
                    {workflow.name}
                  </ActionList.Description>,
                ],
                metadata: workflow,
              })) || []
            }
            selectionVariant="single"
            onSelectedChange={item => {
              const selectedItem = Array.isArray(item) ? item[0] : item
              if (selectedItem) {
                onChange(selectedItem.metadata.path, selectedItem.metadata)
              }
            }}
            loading={workflowOptions === undefined}
            selectedItemIds={[]}
            aria-labelledby={labelId}
          />
        </Autocomplete.Overlay>
      </Autocomplete>
      {displayedValidation && (
        <FormControl.Validation id={validationId} variant="error">
          {displayedValidation}
        </FormControl.Validation>
      )}
      {showLoadError && loadError && (
        <div>
          <Button type="button" size="small" onClick={retryWorkflowSuggestions}>
            Try again
          </Button>
        </div>
      )}
    </FormControl>
  )
}

export function shouldShowRequiredWorkflowRepository(sourceType: SourceType, organization?: SimpleOrganization) {
  return sourceType === 'organization' || organization !== undefined
}

function useWorkflowSuggestions(repository: WorkflowRepository, announceLoadError: boolean, targetableOnly: boolean) {
  const {resolvePath} = useRelativeNavigation()
  const [requestVersion, setRequestVersion] = useState(0)
  const [result, setResult] = useState<{
    repositoryId: number
    workflowOptions: WorkflowFile[]
    loadError: boolean
  }>()

  useEffect(() => {
    let ignore = false
    const repositoryId = repository.id

    const fetchWorkflowSuggestions = async () => {
      try {
        const suggestedWorkflows = await getWorkflowSuggestions(resolvePath('../workflows'), repositoryId)
        if (!ignore) {
          setResult({
            repositoryId,
            workflowOptions: targetableOnly
              ? suggestedWorkflows.filter(workflow => isTargetableRequiredWorkflowPath(workflow.path))
              : suggestedWorkflows,
            loadError: false,
          })
        }
      } catch {
        if (!ignore) {
          setResult({repositoryId, workflowOptions: [], loadError: true})
          if (announceLoadError) {
            announce(WORKFLOW_LOAD_ERROR)
          }
        }
      }
    }

    void fetchWorkflowSuggestions()

    return () => {
      ignore = true
    }
  }, [announceLoadError, repository.id, requestVersion, resolvePath, targetableOnly])

  const retry = useCallback(() => {
    setResult(undefined)
    setRequestVersion(current => current + 1)
  }, [])

  return {
    workflowOptions: result?.repositoryId === repository.id ? result.workflowOptions : undefined,
    loadError: result?.repositoryId === repository.id && result.loadError,
    retry,
  }
}
